import { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown } from "lucide-react";

const LOGO =
  "https://customer-assets.emergentagent.com/job_5d53cc0c-f8e0-4354-807b-347bf58e1216/artifacts/3rlolaxv_Logo.png";

const PRIMARY_NAV = [
  { to: "/", label: "Home", end: true },
  {
    label: "Team",
    match: (p) => p === "/roster" || p.startsWith("/roster/") || p === "/about",
    children: [
      { to: "/roster", label: "Roster" },
      { to: "/about", label: "About Us" },
    ],
  },
  {
    label: "Events",
    match: (p) => p.startsWith("/tournaments") || p === "/live" || p.startsWith("/news"),
    children: [
      { to: "/tournaments", label: "Tournaments" },
      { to: "/live", label: "Live Stream" },
      { to: "/news", label: "News" },
    ],
  },
  {
    label: "Community",
    match: (p) => ["/media", "/sponsors", "/faq", "/contact"].includes(p),
    children: [
      { to: "/media", label: "Media Gallery" },
      { to: "/sponsors", label: "Sponsors" },
      { to: "/faq", label: "FAQ" },
      { to: "/contact", label: "Contact" },
    ],
  },
  { to: "/recruitment", label: "Recruit" },
];

function DropdownNav({ item, pathname }) {
  const [open, setOpen] = useState(false);
  const active = item.match?.(pathname);
  useEffect(() => {
    if (!open) return;
    const close = () => setOpen(false);
    window.addEventListener("click", close);
    return () => window.removeEventListener("click", close);
  }, [open]);

  return (
    <div className="relative" onClick={(e) => e.stopPropagation()}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid={`nav-dropdown-${item.label.toLowerCase()}`}
        className={`inline-flex items-center gap-1 px-4 py-2 text-sm font-semibold uppercase tracking-[0.18em] rounded-full transition-colors ${
          active
            ? "text-white bg-axe-purple/15 border border-axe-purple/50"
            : "text-neutral-300 hover:text-white"
        }`}
      >
        {item.label} <ChevronDown size={12} />
      </button>
      {open && (
        <div className="absolute left-0 mt-2 min-w-[180px] rounded-2xl border border-axe-border bg-axe-surface p-2 z-50 shadow-2xl">
          {item.children.map((c) => (
            <Link
              key={c.to}
              to={c.to}
              onClick={() => setOpen(false)}
              data-testid={`nav-dropdown-link-${c.label.toLowerCase().replace(/\s/g, "-")}`}
              className="block px-4 py-2.5 text-xs uppercase tracking-[0.22em] font-semibold rounded-xl hover:bg-axe-bg text-neutral-200 hover:text-white"
            >
              {c.label}
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  return (
    <header
      className="sticky top-0 z-50 backdrop-blur-xl bg-axe-bg/75 border-b border-axe-border"
      data-testid="site-navbar"
    >
      <div className="max-w-7xl mx-auto px-5 md:px-8 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3 group" data-testid="nav-logo-link">
          <img src={LOGO} alt="AXE" className="h-9 w-9 glow-purple" />
          <div className="leading-none">
            <div className="font-display font-black tracking-[0.25em] text-[15px] text-white">
              AXE
            </div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-axe-tangerine font-semibold">
              Anti Xquad'E
            </div>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-1">
          {PRIMARY_NAV.map((n) =>
            n.children ? (
              <DropdownNav key={n.label} item={n} pathname={pathname} />
            ) : (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.end}
                data-testid={`nav-link-${n.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `px-4 py-2 text-sm font-semibold uppercase tracking-[0.18em] rounded-full transition-colors ${
                    isActive
                      ? "text-white bg-axe-purple/15 border border-axe-purple/50"
                      : "text-neutral-300 hover:text-white"
                  }`
                }
              >
                {n.label}
              </NavLink>
            )
          )}
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          <a
            href="https://discord.gg/antixquade"
            target="_blank"
            rel="noreferrer"
            data-testid="nav-discord-cta"
            className="inline-flex items-center gap-2 rounded-full bg-axe-tangerine px-5 py-2 text-sm font-bold uppercase tracking-[0.18em] text-black hover:bg-axe-tangerine-hover transition-colors drop-shadow-[0_0_12px_rgba(255,140,0,0.45)]"
          >
            Join Discord
          </a>
        </div>

        <button
          type="button"
          aria-label="Toggle menu"
          onClick={() => setOpen((v) => !v)}
          className="lg:hidden p-2 rounded-lg text-white"
          data-testid="nav-mobile-toggle"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden border-t border-axe-border bg-axe-bg max-h-[70vh] overflow-y-auto" data-testid="nav-mobile-panel">
          <div className="px-5 py-4 flex flex-col gap-1">
            {PRIMARY_NAV.flatMap((n) =>
              n.children
                ? [
                    <div key={`${n.label}-header`} className="px-2 pt-3 pb-1 text-[10px] uppercase tracking-[0.3em] text-axe-tangerine font-bold">
                      {n.label}
                    </div>,
                    ...n.children.map((c) => (
                      <Link
                        key={c.to}
                        to={c.to}
                        onClick={() => setOpen(false)}
                        className="px-4 py-3 rounded-xl text-sm uppercase tracking-[0.2em] font-semibold text-neutral-200"
                        data-testid={`nav-mobile-link-${c.label.toLowerCase().replace(/\s/g, "-")}`}
                      >
                        {c.label}
                      </Link>
                    )),
                  ]
                : [
                    <Link
                      key={n.to}
                      to={n.to}
                      onClick={() => setOpen(false)}
                      className={`px-4 py-3 rounded-xl text-sm uppercase tracking-[0.2em] font-semibold ${
                        pathname === n.to
                          ? "bg-axe-purple/15 border border-axe-purple/40 text-white"
                          : "text-neutral-300"
                      }`}
                      data-testid={`nav-mobile-link-${n.label.toLowerCase()}`}
                    >
                      {n.label}
                    </Link>,
                  ]
            )}
            <a
              href="https://discord.gg/antixquade"
              target="_blank"
              rel="noreferrer"
              className="mt-3 text-center rounded-full bg-axe-tangerine py-3 font-bold uppercase tracking-[0.2em] text-black"
              data-testid="nav-mobile-discord-cta"
            >
              Join Discord
            </a>
          </div>
        </div>
      )}
    </header>
  );
}
